// Fetch-Wrapper für /api/v1/admin/*-Aufrufe.
// - Setzt Authorization: Bearer <jwt> (aus authStore)
// - Setzt X-Step-Up-Token wenn vorhanden (für destruktive Aktionen)
// - Wirft bei 401 → Trigger Logout-Flow im Aufrufer
// - Erwartet { success, data } | { success: false, error } Response-Schema

const API_URL = import.meta.env.VITE_API_URL;
if (!API_URL) {
  throw new Error('VITE_API_URL is not set — configure it in .env');
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface FetchOptions extends RequestInit {
  stepUpToken?: string;
}

let getAuthToken: (() => string | null) | null = null;
let onUnauthorized: (() => void) | null = null;

export function configureAuth(getter: () => string | null): void {
  getAuthToken = getter;
}

// Wird bei jedem 401 aus dem Backend aufgerufen — typischerweise um die
// Session zu invalidieren und den User zur Login-Seite zu schicken. Wird in
// authStore.bindAuthToApiClient() einmalig beim App-Start gesetzt.
export function configureUnauthorizedHandler(handler: () => void): void {
  onUnauthorized = handler;
}

export async function apiFetch<T>(
  path: string,
  options: FetchOptions = {},
): Promise<T> {
  const { stepUpToken, headers, body: requestBody, ...rest } = options;
  const token = getAuthToken?.();

  const finalHeaders: Record<string, string> = {
    ...(headers as Record<string, string> | undefined),
  };
  // Content-Type nur setzen, wenn es tatsächlich einen Body gibt — sonst
  // weist Fastify mit FST_ERR_CTP_EMPTY_JSON_BODY ab.
  if (requestBody !== undefined && requestBody !== null) {
    finalHeaders['Content-Type'] = 'application/json';
  }
  if (token) finalHeaders['Authorization'] = `Bearer ${token}`;
  if (stepUpToken) finalHeaders['X-Step-Up-Token'] = stepUpToken;

  const response = await fetch(`${API_URL}${path}`, {
    ...rest,
    body: requestBody as BodyInit | null | undefined,
    headers: finalHeaders,
    credentials: 'include',
  });

  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    // Empty body is OK on 204
  }

  // Das Backend ist gemischt: einige Routen wrappen mit { success, data, error },
  // andere antworten flach (z.B. /auth/* Token-Tausch). Wir unterstützen beide:
  // - { error: 'CODE', message: '...' } → ApiError mit message als Text + code
  // - { success: false, error } → ApiError
  // - { success: true, data: T } → unwrap zu T
  // - sonst → flacher Body wird direkt zurückgegeben
  const wrapper = body as Partial<ApiResponse<T>> & { error?: string; message?: string };

  if (!response.ok) {
    // 401 → Session ist abgelaufen oder Token wurde server-seitig revoked.
    // Zentralen Handler triggern (clearSession + Redirect via ProtectedRoute),
    // damit nicht jede Page ihren eigenen "Invalid or expired access token"-
    // Fehler anzeigen muss.
    if (response.status === 401) {
      onUnauthorized?.();
    }
    // errorHandler.plugin.ts gibt `error: 'FORBIDDEN'` als Code + `message: '...'`
    // als lesbaren Text zurück. Wir bevorzugen message, fallback auf error-code,
    // damit Frontend-Detection (z.B. /multi-factor/i.test) funktioniert.
    throw new ApiError(
      wrapper?.message ?? wrapper?.error ?? response.statusText ?? 'Request failed',
      response.status,
      wrapper?.error,
    );
  }

  if (wrapper && wrapper.success === false) {
    throw new ApiError(wrapper.error ?? 'Unknown error', response.status);
  }

  if (
    wrapper &&
    typeof wrapper === 'object' &&
    'success' in wrapper &&
    wrapper.success === true &&
    'data' in wrapper
  ) {
    return wrapper.data as T;
  }

  return body as T;
}

export const api = {
  get: <T>(path: string, options?: FetchOptions) =>
    apiFetch<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: FetchOptions) =>
    apiFetch<T>(path, {
      ...options,
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    }),
  patch: <T>(path: string, body?: unknown, options?: FetchOptions) =>
    apiFetch<T>(path, {
      ...options,
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    }),
  delete: <T>(path: string, options?: FetchOptions) =>
    apiFetch<T>(path, { ...options, method: 'DELETE' }),
};
